'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useCartStore } from 'components/shared/Cart/store/CartStoreContext';
import { SDD_NDD_BADGE_ENABLED } from 'utils/featureFlags';
import type { SddDeliveryBanner } from 'content/campaigns';
import { SDD_GTM_EVENTS, SDD_PINCODE_STORAGE_KEY } from './config';
import { trackEvent } from 'components/shared/UserAnalytics/funnelEvents';
import {
  fetchDeliveryEstimate,
  IN_PINCODE_RE,
  type SddDeliveryMode,
  type SddServiceabilityData,
  type SddServiceabilityFailure,
} from './api';

type SddTranslator = (key: string, values?: Record<string, string>) => string;

const TICK_MS = 60_000;
const SAME_DAY_END_MINUTES = 11 * 60;
const TWO_HOUR_END_MINUTES = 19 * 60;

export type SddArrivesWhen = 'today' | 'tomorrow';
export type SddDeliveryWindow = SddDeliveryMode;
export type SddSelectionBlocker = 'engraving' | 'open_size';

type BannerFields = {
  bannerTitle: string;
  cta: string;
  placeholder?: string;
  bannerAdvisory?: string;
  orderBy?: string;
};

type WindowFields = {
  deliveryWindow: SddDeliveryWindow;
};

type HeldFields = {
  hadEligible: boolean;
  heldCheckoutEta?: string;
  heldHeroDate?: string;
  heldHeroPincode?: string;
  heldHeroWhen?: SddArrivesWhen;
  heldHeroTime?: string;
  heldEtaWindow?: SddDeliveryWindow;
};

export type SddNddState =
  | { status: 'hidden' }
  | ({
      status: 'idle';
      appliesToSelection: boolean;
      selectionBlocker?: SddSelectionBlocker;
    } & BannerFields &
      WindowFields)
  | ({
      status: 'editing';
      pincode: string;
      fieldError?: string;
      appliesToSelection: boolean;
      selectionBlocker?: SddSelectionBlocker;
    } & BannerFields &
      WindowFields &
      HeldFields)
  | ({
      status: 'loading';
      pincode: string;
      appliesToSelection: boolean;
      selectionBlocker?: SddSelectionBlocker;
    } & BannerFields &
      WindowFields &
      HeldFields)
  | ({
      status: 'eligible';
      pincode: string;
      title: string;
      heroDate?: string;
      heroWhen?: SddArrivesWhen;
      heroTime?: string;
      etaWindow: SddDeliveryWindow;
      advisory?: string;
      mode?: SddDeliveryMode;
      city?: string;
      cutoffIst?: string;
      etaDate?: string;
      orderBy?: string;
      getItBy?: string;
      appliesToSelection: boolean;
      selectionBlocker?: SddSelectionBlocker;
    } & WindowFields)
  | ({
      status: 'ineligible';
      pincode: string;
      title: string;
      city?: string;
      appliesToSelection: boolean;
      selectionBlocker?: SddSelectionBlocker;
    } & BannerFields &
      WindowFields)
  | ({
      status: 'error';
      pincode: string;
      message: string;
      appliesToSelection: boolean;
      selectionBlocker?: SddSelectionBlocker;
    } & BannerFields &
      WindowFields &
      HeldFields);

export type SddNddController = {
  state: SddNddState;
  startCheck: () => void;
  setPincodeInput: (value: string) => void;
  submitPincode: () => void;
  changePincode: () => void;
};

/** IST clock window. Used to refetch at 11:00 / 19:00 / midnight and as
 * fallback when campaigns / serviceability omit `mode`. 11:00 inclusive
 * starts TWO_HOUR; 19:00 inclusive starts NEXT_DAY. */
export const resolveSddWindow = (now: Date = new Date()): SddDeliveryWindow => {
  const { minutesOfDay } = getIstParts(now);
  if (minutesOfDay < SAME_DAY_END_MINUTES) return 'same_day';
  if (minutesOfDay < TWO_HOUR_END_MINUTES) return 'two_hour';
  return 'next_day';
};

export const sddWindowShowsTimer = (window: SddDeliveryWindow): boolean =>
  window !== 'next_day';

/** API `order_by` when present (idle banner or eligible result). */
export const sddTimerDeadline = (state: SddNddState): string | undefined => {
  if (state.status === 'hidden' || state.status === 'ineligible') {
    return undefined;
  }
  return 'orderBy' in state ? state.orderBy : undefined;
};

/**
 * The safety property behind the held express promise, in one place.
 *
 * A held "arrives today" copy may render only when it describes the SKU that
 * is currently selected, or while a check for that SKU is still in flight —
 * that second case is the stale-while-revalidate window that stops the CTA
 * blinking on every colour/size click. Any other combination means the
 * shopper moved to a variant we do not have an answer for, so the warehouse
 * date must show instead.
 *
 * Stated as an invariant on purpose. The alternative, clearing the held copy
 * on every path that ends a check, cannot be made correct: the paths that
 * never reach a terminal exit at all (a sku_change with no usable pincode, or
 * the user pressing Change while a re-check is in flight) have nothing to
 * clear from, and each new early return is another chance to forget.
 */
export const heldPromiseAppliesToSku = (
  heldSku: string | null | undefined,
  inFlightSku: string | null,
  currentSku: string | null,
): boolean => {
  // No resolved SKU means there is no ring to promise anything about. Guarded
  // first because without it two nulls compare equal and would authorise the
  // previous variant's copy against an empty selection.
  if (!currentSku) return false;
  return heldSku === currentSku || inFlightSku === currentSku;
};

/** Engraving is not an express promise. The sizing kit (open_size) is. */
const suppressedByEngraving = (state: SddNddState): boolean =>
  state.status !== 'hidden' && state.selectionBlocker === 'engraving';

/** Warehouse "Dispatched by" only before an eligible result, or after ineligible.
 * Change pincode after eligible keeps warehouse hidden.
 * First-check loading / error do not hide it — the slot reserves the extra
 * meta line so offers do not jump. Engraving: warehouse. Sizing kit: express. */
export function sddReplacesWarehouseEstimate(state: SddNddState): boolean {
  if (suppressedByEngraving(state)) return false;
  if (state.status === 'eligible') return true;
  if (
    state.status === 'editing' ||
    state.status === 'loading' ||
    state.status === 'error'
  ) {
    return state.hadEligible;
  }
  return false;
}

/** Compact ETA for cart summary + bottom bar. Holds last eligible copy
 * through Change pincode until a new ineligible result. Engraving: warehouse. */
export function sddCheckoutEta(state: SddNddState): string | null {
  if (state.status === 'hidden') return null;
  if (suppressedByEngraving(state)) return null;
  if (state.status === 'eligible') return state.getItBy ?? null;
  if (
    (state.status === 'editing' ||
      state.status === 'loading' ||
      state.status === 'error') &&
    state.hadEligible
  ) {
    return state.heldCheckoutEta ?? null;
  }
  return null;
}

/** Buy-section `.shipping-estimate` line: warehouse otherwise. Holds through
 * Change pincode. Engraving: warehouse. Sizing kit: express. */
export function sddHeroTimeline(state: SddNddState): {
  date: string;
  pincode: string;
  when: SddArrivesWhen;
  time?: string;
  window: SddDeliveryWindow;
} | null {
  if (suppressedByEngraving(state)) return null;
  if (state.status === 'eligible') {
    const window = state.etaWindow;
    if (window === 'two_hour') {
      return {
        date: state.heroDate ?? '',
        pincode: state.pincode,
        when: state.heroWhen ?? 'today',
        window,
        ...(state.heroTime ? { time: state.heroTime } : {}),
      };
    }
    if (state.heroDate && state.heroWhen) {
      return {
        date: state.heroDate,
        pincode: state.pincode,
        when: state.heroWhen,
        window,
      };
    }
    return null;
  }
  if (
    (state.status === 'editing' ||
      state.status === 'loading' ||
      state.status === 'error') &&
    state.hadEligible &&
    state.heldEtaWindow &&
    state.heldHeroPincode
  ) {
    const window = state.heldEtaWindow;
    if (window === 'two_hour') {
      return {
        date: state.heldHeroDate ?? '',
        pincode: state.heldHeroPincode,
        when: state.heldHeroWhen ?? 'today',
        window,
        ...(state.heldHeroTime ? { time: state.heldHeroTime } : {}),
      };
    }
    if (state.heldHeroDate && state.heldHeroWhen) {
      return {
        date: state.heldHeroDate,
        pincode: state.heldHeroPincode,
        when: state.heldHeroWhen,
        window,
      };
    }
  }
  return null;
}

export const idleSddNddController: SddNddController = {
  state: { status: 'hidden' },
  startCheck: () => undefined,
  setPincodeInput: () => undefined,
  submitPincode: () => undefined,
  changePincode: () => undefined,
};

type Phase =
  | { kind: 'idle' }
  | { kind: 'editing'; pincode: string; fieldError?: string }
  | { kind: 'loading'; pincode: string }
  | { kind: 'result'; data: SddServiceabilityData }
  | { kind: 'error'; pincode: string; message: string };

type IstParts = { isoDate: string; minutesOfDay: number };

export const getIstParts = (date: Date): IstParts => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    isoDate: `${get('year')}-${get('month')}-${get('day')}`,
    minutesOfDay: Number(get('hour')) * 60 + Number(get('minute')),
  };
};

export const parseCutoffMinutes = (cutoffIst: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(cutoffIst.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return hour * 60 + minute;
};

const parseOrderByMs = (value: string | undefined): number | null => {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
};

/** How long a `sku|pincode` express answer may be replayed. Bounds a
 * long-open tab to about two minutes past the fetch, even if a cutoff
 * tick never runs. */
export const RESULT_CACHE_TTL_MS = 120_000;

export type CachedExpressResult = {
  data: SddServiceabilityData;
  fetchedAt: number;
};

/** Eligible promises die at `order_by`, else at `cutoff_ist`. Ineligible
 * answers have no promise to expire — TTL and window changes cover them. */
const expressPromisePastCutoff = (
  data: SddServiceabilityData,
  nowMs: number,
): boolean => {
  if (!data.eligible) return false;
  const orderByMs = parseOrderByMs(data.order_by);
  if (orderByMs != null) return nowMs >= orderByMs;
  if (!data.cutoff_ist) return false;
  const cutoff = parseCutoffMinutes(data.cutoff_ist);
  if (cutoff == null) return false;
  return getIstParts(new Date(nowMs)).minutesOfDay >= cutoff;
};

export const isExpressCacheFresh = (
  entry: CachedExpressResult,
  nowMs: number,
): boolean => {
  if (nowMs - entry.fetchedAt > RESULT_CACHE_TTL_MS) return false;
  return !expressPromisePastCutoff(entry.data, nowMs);
};

const formatEtaDate = (etaDate: string, locale: string): string | undefined => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(etaDate)) return undefined;
  const parts = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).formatToParts(new Date(`${etaDate}T00:00:00Z`));
  const day = parts.find((part) => part.type === 'day')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  if (!day || !month) return undefined;
  return `${day} ${month}`;
};

/** "2026-09-02T15:00:00+05:30" → "3 PM" (IST). Minutes kept when non-zero. */
export const formatDeliveryByTime = (
  deliveryBy: string | undefined,
): string | undefined => {
  if (!deliveryBy) return undefined;
  const instant = new Date(deliveryBy);
  if (Number.isNaN(instant.getTime())) return undefined;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(instant);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const hour = get('hour');
  const minute = get('minute');
  const dayPeriod = get('dayPeriod').replace(/\./g, '').toUpperCase();
  if (!hour || !dayPeriod) return undefined;
  if (minute === '00') return `${hour} ${dayPeriod}`;
  return `${hour}:${minute} ${dayPeriod}`;
};

const arrivesCheckoutCopy = (
  t: SddTranslator,
  window: SddDeliveryWindow,
  when: SddArrivesWhen,
  date: string,
  time?: string,
): string => {
  if (window === 'two_hour') {
    return time
      ? `${t('ring.sddNdd.arrivesLead')} ${t('ring.sddNdd.byTime', { time })}`
      : t('ring.sddNdd.arrivesInTwoHours');
  }
  /** Literal keys, not a ternary key — check-i18n-keys only sees quoted first args. */
  return when === 'today'
    ? t('ring.sddNdd.arrivesToday', { date })
    : t('ring.sddNdd.arrivesTomorrow', { date });
};

type EligiblePresentation = {
  heroDate?: string;
  heroWhen?: SddArrivesWhen;
  heroTime?: string;
  getItBy?: string;
  etaWindow: SddDeliveryWindow;
};

const presentEligible = (
  data: SddServiceabilityData,
  locale: string,
  t: SddTranslator,
  now: Date,
): EligiblePresentation => {
  const etaWindow = data.mode ?? resolveSddWindow(now);
  const date = data.eta_date ? formatEtaDate(data.eta_date, locale) : undefined;
  if (etaWindow === 'two_hour') {
    const heroTime = formatDeliveryByTime(data.delivery_by);
    return {
      heroDate: date,
      heroWhen: 'today',
      ...(heroTime ? { heroTime } : {}),
      getItBy: arrivesCheckoutCopy(t, etaWindow, 'today', date ?? '', heroTime),
      etaWindow,
    };
  }
  const heroWhen: SddArrivesWhen =
    etaWindow === 'next_day' ? 'tomorrow' : 'today';
  return {
    heroDate: date,
    heroWhen,
    getItBy: date
      ? arrivesCheckoutCopy(t, etaWindow, heroWhen, date)
      : undefined,
    etaWindow,
  };
};

const sanitizePincode = (raw: string): string =>
  raw.replace(/\D/g, '').slice(0, 6);

const pinFromPhase = (phase: Phase): string | null => {
  if (phase.kind === 'result') return phase.data.pincode;
  if (
    phase.kind === 'editing' ||
    phase.kind === 'loading' ||
    phase.kind === 'error'
  ) {
    return phase.pincode || null;
  }
  return null;
};

const storedSddPincode = (phasePin: string | null): string | null => {
  if (phasePin && IN_PINCODE_RE.test(phasePin)) return phasePin;
  try {
    const stored = sessionStorage.getItem(SDD_PINCODE_STORAGE_KEY);
    return stored && IN_PINCODE_RE.test(stored) ? stored : null;
  } catch {
    return null;
  }
};

/** Unlike storedSddPincode, never falls back to sessionStorage. Used by the
 * sku_change effect: a user who just hit "Change" and is mid-typing a NEW
 * pincode (phase 'editing' with a partial/invalid value, e.g. "11") must not
 * have that overwritten by a stale, previously-confirmed pincode restored
 * from storage when they switch colour/size. */
const activePhasePincode = (phase: Phase): string | null => {
  const pin = pinFromPhase(phase);
  return pin && IN_PINCODE_RE.test(pin) ? pin : null;
};

// GTM + Vercel (sdd_ndd_* snake_case).
const pushToDataLayer = trackEvent;

/** Who started a serviceability check: a user action or an automatic re-run. */
export type SddCheckTrigger =
  | 'typed'
  | 'submit'
  | 'restore'
  | 'window_change'
  | 'cutoff'
  | 'sku_change';

const bannerFields = (banner: SddDeliveryBanner): BannerFields => ({
  bannerTitle: banner.title,
  cta: banner.cta,
  placeholder: banner.placeholder,
  bannerAdvisory: banner.advisory,
  ...(banner.order_by ? { orderBy: banner.order_by } : {}),
});

export function useSddNddEligibility(args: {
  engravingSelected: boolean;
  isOpenSize: boolean;
  /** Canonical SKU for the current colour+size (components/ring-air/utils/checkout.tsx skuFor). */
  sku: string | null;
}): SddNddController {
  // Module constant — same branch every render in a given build.
  if (!SDD_NDD_BADGE_ENABLED) return idleSddNddController;
  // eslint-disable-next-line react-hooks/rules-of-hooks -- compile-time flag
  return useSddNddEligibilityRuntime(args);
}

function useSddNddEligibilityRuntime(args: {
  engravingSelected: boolean;
  isOpenSize: boolean;
  sku: string | null;
}): SddNddController {
  const { engravingSelected, isOpenSize, sku } = args;
  // main read these off CartContext; this branch's cart state lives in Zustand.
  const sddDelivery = useCartStore((s) => s.sddDelivery);
  const refetchCampaigns = useCartStore((s) => s.refetchCampaigns);
  const t = useTranslations('ring') as unknown as SddTranslator;
  const locale = useLocale();

  const selectionBlocker: SddSelectionBlocker | undefined = engravingSelected
    ? 'engraving'
    : isOpenSize
      ? 'open_size'
      : undefined;

  const enabled = SDD_NDD_BADGE_ENABLED && sddDelivery !== null;
  const banner = enabled ? sddDelivery : null;

  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  // `sku` is what the held copy DESCRIBES. Without it, every code path that
  // ends a check has to remember to clear this, and the ones that never reach
  // a terminal exit at all (a sku_change with no usable pincode, a user
  // hitting Change while a re-check is in flight) silently cannot — which is
  // how a previous variant's same-day promise ends up rendered against a
  // different ring. Carrying the identity makes the question "does this copy
  // belong to what is selected right now?" answerable at render time instead
  // of depending on exhaustive clearing. See `heldAppliesToSku` below.
  const [heldEligible, setHeldEligible] = useState<{
    hadEligible: boolean;
    sku?: string | null;
    checkoutEta?: string;
    heroDate?: string;
    heroPincode?: string;
    heroWhen?: SddArrivesWhen;
    heroTime?: string;
    etaWindow?: SddDeliveryWindow;
  }>({ hadEligible: false });
  const [deliveryWindow, setDeliveryWindow] = useState(resolveSddWindow);
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);
  const lastResultRef = useRef<SddServiceabilityData | null>(null);
  const restoredRef = useRef(false);
  const shownRef = useRef(false);
  const cutoffRefetchRef = useRef(false);
  const windowRef = useRef<SddDeliveryWindow>(deliveryWindow);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  // runCheck reads the SKU off this ref (not a closure/arg) so restore /
  // window_change / cutoff refetches always send the CURRENTLY selected
  // variant's SKU, never one captured when the callback was created.
  const skuRef = useRef(sku);
  skuRef.current = sku;
  // Tracks the previous render's sku so the effect below fires exactly once
  // per actual colour/size change, not on every unrelated re-render.
  const prevSkuRef = useRef(sku);
  // Non-null only while a delivery_estimate request for that sku is in the
  // air. Paired with heldEligible.sku by `heldAppliesToSku` below.
  const inFlightSkuRef = useRef<string | null>(null);
  // Session-lived `sku|pincode` -> last successful express result, with the
  // time it was fetched. Lets a user re-selecting an already-checked
  // colour/size resolve synchronously (no fetch, no loading phase) instead
  // of re-hitting delivery_estimate on every click of a scan like 5→14→5.
  // Entries older than RESULT_CACHE_TTL_MS, or whose own order_by /
  // cutoff_ist has passed, are a miss. The tick below drops those on every
  // interval regardless of phase. The whole map is also cleared on
  // window_change / cutoff, because those exist specifically because
  // time-of-day eligibility changed.
  const resultCacheRef = useRef<Map<string, CachedExpressResult>>(new Map());

  const rememberEligible = useCallback(
    (data: SddServiceabilityData) => {
      if (!data.eligible) {
        setHeldEligible({ hadEligible: false });
        return;
      }
      const presented = presentEligible(data, locale, t, new Date());
      setHeldEligible({
        hadEligible: true,
        sku: skuRef.current,
        checkoutEta: presented.getItBy,
        heroPincode: data.pincode,
        etaWindow: presented.etaWindow,
        ...(presented.heroDate ? { heroDate: presented.heroDate } : {}),
        ...(presented.heroWhen ? { heroWhen: presented.heroWhen } : {}),
        ...(presented.heroTime ? { heroTime: presented.heroTime } : {}),
      });
    },
    [locale, t],
  );

  // Shared tail of a successful check (live fetch or cache hit): persist the
  // pincode, re-arm the cutoff watcher, remember eligibility, and commit the
  // result phase in one shot.
  const applyExpressResult = useCallback(
    (express: SddServiceabilityData, pincode: string) => {
      try {
        sessionStorage.setItem(SDD_PINCODE_STORAGE_KEY, pincode);
      } catch {}
      const orderByMs = parseOrderByMs(express.order_by);
      if (orderByMs != null) {
        cutoffRefetchRef.current = Date.now() >= orderByMs;
      } else {
        const nextCutoff = express.cutoff_ist
          ? parseCutoffMinutes(express.cutoff_ist)
          : null;
        cutoffRefetchRef.current =
          nextCutoff != null &&
          getIstParts(new Date()).minutesOfDay >= nextCutoff;
      }
      rememberEligible(express);
      lastResultRef.current = express;
      setPhase({ kind: 'result', data: express });
    },
    [rememberEligible],
  );

  // Shared tail of a check that ended WITHOUT a fresh answer (transport
  // failure, unusable payload, unresolvable SKU). The held copy is what keeps
  // the previous variant's promise on screen while a new request is in
  // flight — that's deliberate, it's what removed the blink. But once a
  // sku_change check has terminated with no answer, nothing is in flight any
  // more and the held promise belongs to a DIFFERENT colour/size: keeping it
  // would show a same-day date for a variant we never successfully checked.
  // So retire it here and let the warehouse "Dispatched by" line come back.
  //
  // Scoped to sku_change on purpose. For every other trigger the SKU is
  // unchanged, so the held copy still describes the selected variant, and
  // holding it through a pincode edit is the existing documented behaviour
  // (see sddReplacesWarehouseEstimate).
  //
  // Every terminal non-result exit in runCheck goes through this, so a new
  // early return can't silently reintroduce the stale promise.
  const applyFailure = useCallback((phase: Phase, isSkuChange: boolean) => {
    if (isSkuChange) setHeldEligible({ hadEligible: false });
    setPhase(phase);
  }, []);

  const runCheck = useCallback(
    async (pincode: string, trigger: SddCheckTrigger = 'submit') => {
      const requestSku = skuRef.current;

      // Session cache hit: resolve synchronously, no fetch, no GTM noise.
      // window_change / cutoff bypass it on purpose — those triggers exist
      // BECAUSE the time-of-day window just changed, so a cache entry
      // captured under the old window would silently reapply stale
      // eligibility for every sku.
      const cacheKey = requestSku ? `${requestSku}|${pincode}` : null;
      const cacheable = trigger !== 'window_change' && trigger !== 'cutoff';
      if (cacheKey && cacheable) {
        const cached = resultCacheRef.current.get(cacheKey);
        if (cached && isExpressCacheFresh(cached, Date.now())) {
          // A previous sku_change's fetch may still be in flight; a cache
          // hit for a DIFFERENT (now current) sku must win the race.
          abortRef.current?.abort();
          requestIdRef.current += 1;
          applyExpressResult(cached.data, pincode);
          return;
        }
        if (cached) resultCacheRef.current.delete(cacheKey);
      }

      // Held-state semantics: holding the last eligible copy through a
      // pincode edit / clock-driven refetch (window_change, cutoff) is the
      // existing, intended UX — the SKU hasn't changed, so the promise is
      // still live while we confirm it. sku_change is stale-while-revalidate
      // instead: the colour/size just switched, but the OLD sku's resolved
      // CTA (badge pill / bottom-bar ETA / accordion warehouse line) stays
      // on screen until the NEW sku's result lands and replaces it in one
      // shot below — neither heldEligible nor the phase flips to a loading/
      // cleared state first. Clearing both synchronously (the previous
      // behaviour) is what produced the "blink to a different CTA and back"
      // on every size/colour click; every OTHER trigger is a direct user
      // action (typed/submit/restore/window_change/cutoff) and keeps the
      // existing loading UX.
      const isSkuChange = trigger === 'sku_change';

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const requestId = ++requestIdRef.current;
      const isStale = () =>
        controller.signal.aborted || requestId !== requestIdRef.current;
      if (!requestSku) {
        // No SKU resolved yet (e.g. size not chosen) — a request is a
        // guaranteed 400. Skip the network call and the GTM noise, keep the
        // pincode field as-is. Still a terminal end with no fresh answer, so
        // it retires a sku_change's held promise like any other failure.
        applyFailure({ kind: 'editing', pincode }, isSkuChange);
        return;
      }

      // Every result now comes from the SKU-aware delivery_estimate endpoint.
      // The old pincode-only sdd_serviceability fallback is gone: a 404 used
      // to mean "not deployed to staging yet", but both staging and prod now
      // answer 200 (prod returns eligible:false even for an unknown SKU), so
      // a 404 means something is genuinely wrong and must not be permission
      // to promise same-day off an endpoint that never sees the SKU. Kept as
      // a constant so the GA4 field shape is unchanged and a value other than
      // 'combined' would be an obvious regression.
      const source = 'combined' as const;

      if (!isSkuChange) {
        setPhase({ kind: 'loading', pincode });
      }
      pushToDataLayer(SDD_GTM_EVENTS.CHECK, {
        pincode,
        trigger,
        sku: requestSku,
      });
      // Which sku has a check in the air. This is the ONLY reason a held copy
      // belonging to a different sku is still allowed to render: we are
      // actively confirming the current one and deliberately avoiding a
      // blink. The `finally` below clears it on EVERY exit — including the
      // isStale() bails, which return without reaching applyFailure — so an
      // aborted or superseded request can never leave a previous variant's
      // promise authorised indefinitely.
      inFlightSkuRef.current = requestSku;
      try {
        let express: SddServiceabilityData | null = null;
        let failure: SddServiceabilityFailure | null = null;

        const primary = await fetchDeliveryEstimate({
          sku: requestSku,
          pincode,
          signal: controller.signal,
        });
        if (isStale()) return;

        if (primary.ok) {
          // `primary.data.standard` (fresher per-SKU warehouse ETA) is
          // intentionally not consumed here: getShippingEstimate() already
          // derives a per-SKU estimate reactively from the SSR `estimates`
          // map, so this stays the source of truth for now — wiring
          // `standard` through would mean threading it into that function's
          // signature too, out of scope for the kiosk-stock badge itself.
          express = primary.data.express;
        } else {
          failure = primary;
        }

        if (failure) {
          if (failure.code === 'invalid_pincode') {
            applyFailure(
              {
                kind: 'editing',
                pincode,
                fieldError: failure.message || t('ring.sddNdd.invalidPincode'),
              },
              isSkuChange,
            );
            pushToDataLayer(SDD_GTM_EVENTS.RESULT, {
              eligible: false,
              reason: 'invalid_pincode',
              pincode,
              trigger,
              sku: requestSku,
              source,
            });
            return;
          }
          if (failure.code === 'invalid_sku') {
            // Missing/unresolved SKU (e.g. size not yet picked) is a
            // client-state gap, not a user-facing error — fall back to the
            // default ETA quietly, keep the pincode field as-is.
            applyFailure({ kind: 'editing', pincode }, isSkuChange);
            pushToDataLayer(SDD_GTM_EVENTS.RESULT, {
              eligible: false,
              reason: 'invalid_sku',
              pincode,
              trigger,
              sku: requestSku,
              source,
            });
            return;
          }
          applyFailure(
            {
              kind: 'error',
              pincode,
              message: failure.message || t('ring.sddNdd.networkError'),
            },
            isSkuChange,
          );
          pushToDataLayer(SDD_GTM_EVENTS.RESULT, {
            eligible: false,
            reason: failure.code,
            pincode,
            trigger,
            sku: requestSku,
            source,
          });
          return;
        }

        if (!express) {
          // status: 'ok' but no usable serviceability payload (kiosk stock
          // missing/stale server-side) — same outcome as a transport
          // failure: default ETA, no stale copy, no crash.
          applyFailure(
            {
              kind: 'error',
              pincode,
              message: t('ring.sddNdd.networkError'),
            },
            isSkuChange,
          );
          pushToDataLayer(SDD_GTM_EVENTS.RESULT, {
            eligible: false,
            reason: 'bad_response',
            pincode,
            trigger,
            sku: requestSku,
            source,
          });
          return;
        }

        // Cache the resolved express result for this sku|pincode (eligible
        // or not — an ineligible/OOS result is still a valid, reusable
        // answer) so re-selecting this sku later in the session is a cache
        // hit, not another fetch.
        if (cacheKey) {
          resultCacheRef.current.set(cacheKey, {
            data: express,
            fetchedAt: Date.now(),
          });
        }
        applyExpressResult(express, pincode);
        pushToDataLayer(SDD_GTM_EVENTS.RESULT, {
          eligible: express.eligible,
          mode: express.mode,
          city: express.city,
          pincode,
          trigger,
          sku: requestSku,
          source,
        });
      } catch {
        if (isStale()) return;
        applyFailure(
          {
            kind: 'error',
            pincode,
            message: t('ring.sddNdd.networkError'),
          },
          isSkuChange,
        );
        pushToDataLayer(SDD_GTM_EVENTS.RESULT, {
          eligible: false,
          reason: 'network',
          pincode,
          trigger,
          sku: requestSku,
          source,
        });
      } finally {
        // Guarded: a newer check for a different sku may already own the
        // marker by the time this one unwinds, and it must keep it.
        if (inFlightSkuRef.current === requestSku) {
          inFlightSkuRef.current = null;
        }
      }
    },
    [t, applyExpressResult, applyFailure],
  );

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!banner || restoredRef.current) return;
    restoredRef.current = true;
    try {
      const stored = sessionStorage.getItem(SDD_PINCODE_STORAGE_KEY);
      if (stored && IN_PINCODE_RE.test(stored)) {
        void runCheck(stored, 'restore');
      }
    } catch {}
  }, [banner, runCheck]);

  useEffect(() => {
    if (!banner) return;
    const tick = () => {
      const nowMs = Date.now();
      // Phase-independent. A cached "Arrives today" must not replay after
      // its own cutoff just because the badge is mid-edit or showing a
      // different SKU. The live refetch below stays on an eligible result
      // so an in-progress pincode edit is not clobbered.
      for (const [key, entry] of resultCacheRef.current) {
        if (!isExpressCacheFresh(entry, nowMs)) {
          resultCacheRef.current.delete(key);
        }
      }
      const nextWindow = resolveSddWindow();
      if (windowRef.current !== nextWindow) {
        windowRef.current = nextWindow;
        setDeliveryWindow(nextWindow);
        refetchCampaigns();
        // Every cached entry was resolved under the OLD window; the API's
        // eligibility/mode for a given sku|pincode can change the instant
        // the window does, so none of them can be trusted anymore.
        resultCacheRef.current.clear();
        const pin = storedSddPincode(pinFromPhase(phaseRef.current));
        if (pin) void runCheck(pin, 'window_change');
        return;
      }
      const current = phaseRef.current;
      if (current.kind !== 'result' || !current.data.eligible) return;
      const resultMode = current.data.mode ?? resolveSddWindow();
      if (!sddWindowShowsTimer(resultMode)) return;
      if (cutoffRefetchRef.current) return;
      const orderByMs = parseOrderByMs(current.data.order_by);
      if (orderByMs != null) {
        if (nowMs >= orderByMs) {
          cutoffRefetchRef.current = true;
          resultCacheRef.current.clear();
          void runCheck(current.data.pincode, 'cutoff');
        }
        return;
      }
      if (!current.data.cutoff_ist) return;
      const cutoff = parseCutoffMinutes(current.data.cutoff_ist);
      if (cutoff == null) return;
      if (getIstParts(new Date(nowMs)).minutesOfDay >= cutoff) {
        cutoffRefetchRef.current = true;
        resultCacheRef.current.clear();
        void runCheck(current.data.pincode, 'cutoff');
      }
    };
    const id = setInterval(tick, TICK_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [banner, refetchCampaigns, runCheck]);

  // Colour/size switch: re-check the NEW sku against the pincode the
  // CURRENT phase already carries (a confirmed 'result' pincode, or an
  // 'editing' pincode that fully validates) — deliberately never falls back
  // to sessionStorage here (unlike restore/window_change), or a user
  // mid-typing a replacement pincode after "Change" would have their
  // in-progress edit clobbered by a stale, previously-confirmed one. No-ops
  // on mount (prevSkuRef seeds to the initial sku) and when no valid pincode
  // is known yet (nothing to re-check against — the badge still shows the
  // default ETA).
  useEffect(() => {
    if (prevSkuRef.current === sku) return;
    prevSkuRef.current = sku;
    if (!sku) {
      // SKU became unresolved (e.g. a disabled colour/size combo) — the
      // previous SKU's held eligible copy must not keep rendering with no
      // SKU selected. Clear it, but — mirroring runCheck's own null-sku
      // short-circuit — keep the best-known pincode around in an 'editing'
      // phase rather than dropping to 'idle' and losing it: restoredRef is
      // already true by now, so sessionStorage restore never re-runs, and
      // this is what lets a LATER sku_change (once the SKU resolves again)
      // self-heal via activePhasePincode instead of forcing a retype. Only
      // falls back to 'idle' when no pincode is known at all.
      setHeldEligible({ hadEligible: false });
      const knownPincode = storedSddPincode(pinFromPhase(phaseRef.current));
      setPhase(
        knownPincode
          ? { kind: 'editing', pincode: knownPincode }
          : { kind: 'idle' },
      );
      return;
    }
    if (!banner) return;
    const pin = activePhasePincode(phaseRef.current);
    if (pin) void runCheck(pin, 'sku_change');
  }, [sku, banner, runCheck]);

  const startCheck = useCallback(() => {
    setPhase({ kind: 'editing', pincode: '' });
  }, []);

  const setPincodeInput = useCallback(
    (value: string) => {
      const pincode = sanitizePincode(value);
      setPhase({ kind: 'editing', pincode });
      if (pincode.length === 6) {
        if (IN_PINCODE_RE.test(pincode)) {
          void runCheck(pincode, 'typed');
        } else {
          setPhase({
            kind: 'editing',
            pincode,
            fieldError: t('ring.sddNdd.invalidPincode'),
          });
          pushToDataLayer(SDD_GTM_EVENTS.RESULT, {
            eligible: false,
            reason: 'invalid_format',
            pincode,
            trigger: 'typed',
            sku: skuRef.current ?? undefined,
          });
        }
      }
    },
    [runCheck, t],
  );

  // Accepts 'error' and ineligible result phases too: the card keeps the
  // pincode in the field there, and Check re-submits it.
  const submitPincode = useCallback(() => {
    const pincode =
      phase.kind === 'editing' || phase.kind === 'error'
        ? phase.pincode
        : phase.kind === 'result' && !phase.data.eligible
          ? phase.data.pincode
          : null;
    if (pincode === null) return;
    if (!IN_PINCODE_RE.test(pincode)) {
      setPhase({
        kind: 'editing',
        pincode,
        fieldError: t('ring.sddNdd.invalidPincode'),
      });
      pushToDataLayer(SDD_GTM_EVENTS.RESULT, {
        eligible: false,
        reason: 'invalid_format',
        pincode,
        trigger: 'submit',
        sku: skuRef.current ?? undefined,
      });
      return;
    }
    void runCheck(pincode, 'submit');
  }, [phase, runCheck, t]);

  const changePincode = useCallback(() => {
    pushToDataLayer(SDD_GTM_EVENTS.CHANGE, {
      pincode: pinFromPhase(phase) ?? '',
      sku: skuRef.current ?? undefined,
    });
    abortRef.current?.abort();
    const previous =
      phase.kind === 'editing' ||
      phase.kind === 'loading' ||
      phase.kind === 'error'
        ? phase.pincode
        : phase.kind === 'result'
          ? phase.data.pincode
          : '';
    setPhase({ kind: 'editing', pincode: previous });
  }, [phase]);

  const state: SddNddState = useMemo(() => {
    if (!banner) return { status: 'hidden' };
    const apiWindow: SddDeliveryWindow | undefined =
      (phase.kind === 'result' ? phase.data.mode : undefined) ?? banner.mode;
    const windowFields = { deliveryWindow: apiWindow ?? deliveryWindow };
    const selection = {
      appliesToSelection: !selectionBlocker,
      selectionBlocker,
    };
    const held = {
      hadEligible:
        heldEligible.hadEligible &&
        heldPromiseAppliesToSku(heldEligible.sku, inFlightSkuRef.current, sku),
      ...(heldEligible.checkoutEta
        ? { heldCheckoutEta: heldEligible.checkoutEta }
        : {}),
      ...(heldEligible.etaWindow
        ? { heldEtaWindow: heldEligible.etaWindow }
        : {}),
      ...(heldEligible.heroPincode
        ? {
            heldHeroPincode: heldEligible.heroPincode,
            ...(heldEligible.heroDate
              ? { heldHeroDate: heldEligible.heroDate }
              : {}),
            ...(heldEligible.heroWhen
              ? { heldHeroWhen: heldEligible.heroWhen }
              : {}),
            ...(heldEligible.heroTime
              ? { heldHeroTime: heldEligible.heroTime }
              : {}),
          }
        : {}),
    };
    const bannerUi = bannerFields(banner);
    if (phase.kind === 'idle') {
      return { status: 'idle', ...bannerUi, ...selection, ...windowFields };
    }
    if (phase.kind === 'editing') {
      return {
        status: 'editing',
        pincode: phase.pincode,
        fieldError: phase.fieldError,
        ...held,
        ...bannerUi,
        ...selection,
        ...windowFields,
      };
    }
    if (phase.kind === 'loading') {
      return {
        status: 'loading',
        pincode: phase.pincode,
        ...held,
        ...bannerUi,
        ...selection,
        ...windowFields,
      };
    }
    if (phase.kind === 'error') {
      return {
        status: 'error',
        pincode: phase.pincode,
        message: phase.message,
        ...held,
        ...bannerUi,
        ...selection,
        ...windowFields,
      };
    }
    const { data } = phase;
    if (!data.eligible) {
      return {
        status: 'ineligible',
        pincode: data.pincode,
        title: data.title,
        city: data.city,
        ...bannerUi,
        ...selection,
        ...windowFields,
      };
    }
    return {
      status: 'eligible',
      pincode: data.pincode,
      title: data.title,
      heroDate: heldEligible.heroDate,
      heroWhen: heldEligible.heroWhen,
      etaWindow: heldEligible.etaWindow ?? apiWindow ?? deliveryWindow,
      ...(heldEligible.heroTime ? { heroTime: heldEligible.heroTime } : {}),
      advisory: data.advisory ?? banner.advisory,
      mode: data.mode,
      city: data.city,
      cutoffIst: data.cutoff_ist,
      etaDate: data.eta_date,
      ...(data.order_by ? { orderBy: data.order_by } : {}),
      getItBy: heldEligible.checkoutEta,
      ...selection,
      ...windowFields,
    };
    // `sku` joins the deps so the identity gate above re-evaluates the
    // moment the selection changes, not only when a check resolves.
  }, [banner, phase, selectionBlocker, heldEligible, deliveryWindow, sku]);

  useEffect(() => {
    if (shownRef.current) return;
    if (state.status === 'hidden') return;
    shownRef.current = true;
    pushToDataLayer(SDD_GTM_EVENTS.SHOWN, { status: state.status });
  }, [state.status]);

  return useMemo(
    () => ({
      state,
      startCheck,
      setPincodeInput,
      submitPincode,
      changePincode,
    }),
    [state, startCheck, setPincodeInput, submitPincode, changePincode],
  );
}
